import React from 'react';
import { useSelector } from 'react-redux';
import {
  Ad,
  AppContent,
  baseUnit,
  DisableMainCategory,
  ErrorBoundary,
  MEDIA_BREAKPOINTS,
  RecommendationsAdvertisement,
  TitleWithSubtitleAndDivider,
  WidgetContentAndSidebar,
} from 'mm-ui-components';
import { Store } from 'redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Theme } from '../../components/connectedComponents/Theme';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { SLOT_NAMES } from '../templates.const';
import { Page } from '../Page';
import { getAdSlotId, getAdSlotOnSuccess, getCanonicalUrl, getExperiments } from '../../store/config/config.selectors';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';
import {
  getMetadataDescription,
  getMetadataImage,
  getMetadataTitle,
  getOutbrainWidgetID,
  getSocialSharingDescription,
  getSocialSharingImage,
  getSocialSharingTitle,
  getSubtitle,
  getTitle,
  getWidgetHTML,
} from '../../store/template/widgetPage/widgetPageGlasgow.selectors';
import { getShouldSetMinHeightForTopAd } from '../../store/template/postPage/postPageOdesa/postPageOdesa.selectors';

interface WidgetPageGlasgowProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

export const WidgetPageGlasgowComponent: React.FunctionComponent<WidgetPageGlasgowProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <WidgetPageGlasgowContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

const createStyle = () => StyleSheet.create({
  wrapper: {
    display: 'grid',
    maxWidth: '1440px',
    width: 'auto',
    marginTop: `${baseUnit * 1.5}px`,
    marginBottom: 0,
    [MEDIA_BREAKPOINTS.large]: {
      marginRight: `${baseUnit * 1.5}px`,
      marginLeft: `${baseUnit * 1.5}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      marginRight: `${baseUnit}px`,
      marginLeft: `${baseUnit}px`,
      display: 'block',
    },
    [MEDIA_BREAKPOINTS.small]: {
      marginRight: `${baseUnit}px`,
      marginLeft: `${baseUnit}px`,
      display: 'block',
    },
  },
});

export const WidgetPageGlasgowContentConnected: React.FunctionComponent = () => {
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const sideBar1Id = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_1));
  const sideBar2Id = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_2));
  const stickyBottomId = useSelector(getAdSlotId(SLOT_NAMES.STICKY_BOTTOM));
  const topAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.TOP_AD));
  const oneOnOneOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.ONE_ON_ONE));
  const sideBar1AdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.SIDE_BAR_1));
  const sideBar2AdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.SIDE_BAR_2));
  const stickyBottomOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.STICKY_BOTTOM));
  const metadataDescription = useSelector(getMetadataDescription);
  const metadataTitle = useSelector(getMetadataTitle);
  const metadataImage = useSelector(getMetadataImage);
  const socialSharingDescription = useSelector(getSocialSharingDescription);
  const socialSharingTitle = useSelector(getSocialSharingTitle);
  const socialSharingImage = useSelector(getSocialSharingImage);
  const outbrainWidgetID = useSelector(getOutbrainWidgetID);
  const canonicalUrl = useSelector(getCanonicalUrl);
  const widgetHTML = useSelector(getWidgetHTML);
  const title = useSelector(getTitle);
  const subtitle = useSelector(getSubtitle);
  const shouldSetMinHeightForTopAd = useSelector(getShouldSetMinHeightForTopAd);
  const experiments = useSelector(getExperiments);

  const [mainArticleURL, setMainArticleURL] = React.useState('');

  React.useEffect(() => {
    if (!mainArticleURL && window) {
      setMainArticleURL(window.location.href);
    }
  }, [mainArticleURL]);

  const url = mainArticleURL || canonicalUrl;

  const style = createStyle();
  return (
    <React.Fragment>
      <MetaTags
        title={socialSharingTitle || metadataTitle}
        description={socialSharingDescription || metadataDescription}
        pageImage={socialSharingImage || metadataImage}
      />
      <HeadScripts />
      <Theme>
        <DisableMainCategory>
          <AppContent>
            <NavigationLayout mainWrapperConfigThemeKey="mainContent">
              <Ad id={topAdId} onSuccess={topAdOnSuccess} setMinHeight={shouldSetMinHeightForTopAd} />
              <div className={css(style.wrapper)}>
                {(title || subtitle) ? <TitleWithSubtitleAndDivider title={title} subtitle={subtitle} /> : null}
                <WidgetContentAndSidebar
                  adId1={sideBar1Id}
                  adId2={sideBar2Id}
                  adOnSuccess1={sideBar1AdOnSuccess}
                  adOnSuccess2={sideBar2AdOnSuccess}
                >
                  <figure dangerouslySetInnerHTML={{ __html: widgetHTML }} />
                </WidgetContentAndSidebar>
                {outbrainWidgetID
                  ? <RecommendationsAdvertisement experiment={experiments[0]} outbrainWidgetID={outbrainWidgetID} pageLiveUrl={url} disableDefaultStyle /> : null}
              </div>
              <Ad id={oneOnOneId} onSuccess={oneOnOneOnSuccess} />
            </NavigationLayout>
            <Ad id={stickyBottomId} onSuccess={stickyBottomOnSuccess} isSticky />
          </AppContent>
        </DisableMainCategory>
      </Theme>
    </React.Fragment>
  );
};
